import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { channelsService, Channel } from '../../services/channels.service'
import AddChannelModal from '../../components/channels/AddChannelModal'
import ChannelCard from '../../components/channels/ChannelCard'
import ChannelDiscoveryModal from '../../components/channels/ChannelDiscoveryModal'
import toast from 'react-hot-toast'

export default function ChannelsPage() {
  const queryClient = useQueryClient()
  const [showAddModal, setShowAddModal] = useState(false)
  const [showDiscoveryModal, setShowDiscoveryModal] = useState(false)

  // Fetch channels
  const { data: channels = [], isLoading } = useQuery({
    queryKey: ['channels'],
    queryFn: () => channelsService.getChannels(),
  })

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['channelStats'],
    queryFn: () => channelsService.getStats(),
  })

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: ({ channelId, isActive }: { channelId: number; isActive: boolean }) =>
      channelsService.updateChannel(channelId, { is_active: isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] })
      queryClient.invalidateQueries({ queryKey: ['channelStats'] })
    },
    onError: () => {
      toast.error('Ошибка при обновлении канала')
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (channelId: number) => channelsService.deleteChannel(channelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] })
      queryClient.invalidateQueries({ queryKey: ['channelStats'] })
      toast.success('Канал удалён')
    },
    onError: () => {
      toast.error('Ошибка при удалении канала')
    },
  })

  // Parse mutation
  const parseMutation = useMutation({
    mutationFn: (channelId: number) => channelsService.parseChannel(channelId),
    onSuccess: (data) => {
      toast.success(data.message)
    },
    onError: () => {
      toast.error('Ошибка при парсинге канала')
    },
  })

  const handleToggleActive = (channel: Channel) => {
    toggleActiveMutation.mutate({
      channelId: channel.id,
      isActive: channel.status !== 'monitored',
    })
  }

  const handleDelete = (channelId: number) => {
    if (window.confirm('Вы уверены, что хотите удалить этот канал?')) {
      deleteMutation.mutate(channelId)
    }
  }

  const handleParse = (channelId: number) => {
    parseMutation.mutate(channelId)
  }

  // Update auto-parse settings mutation
  const updateAutoParseMutation = useMutation({
    mutationFn: ({ channelId, data }: { channelId: number; data: { auto_parse_enabled?: boolean; parsing_interval?: number } }) =>
      channelsService.updateChannel(channelId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] })
    },
    onError: () => {
      toast.error('Ошибка при обновлении настроек')
    },
  })

  const handleUpdateAutoParse = (channelId: number, data: { auto_parse_enabled?: boolean; parsing_interval?: number }) => {
    updateAutoParseMutation.mutate({ channelId, data })
  }

  const activeChannels = channels.filter((c) => c.status === 'monitored')
  const inactiveChannels = channels.filter((c) => c.status !== 'monitored')

  return (
    <div className="pt-14">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Каналы</h1>
          <p className="text-gray-600 mt-1">
            Управление каналами для мониторинга
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowDiscoveryModal(true)}
            className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
          >
            🔍 Найти каналы
          </button>
          <button onClick={() => setShowAddModal(true)} className="btn-primary">
            + Добавить канал
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Всего каналов" value={stats.total_channels} />
          <StatCard label="Активных" value={stats.active_channels} color="green" />
          <StatCard label="Всего лидов" value={stats.total_leads} color="blue" />
          <StatCard label="Лидов сегодня" value={stats.leads_today} color="purple" />
        </div>
      )}

      {/* Channels List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : channels.length === 0 ? (
        <div className="card text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg
              className="mx-auto h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Каналы не добавлены
          </h3>
          <p className="text-gray-600 mb-4">
            Добавьте Telegram каналы для мониторинга и поиска лидов
          </p>
          <div className="flex justify-center gap-2">
            <button
              onClick={() => setShowDiscoveryModal(true)}
              className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
            >
              Найти каналы
            </button>
            <button onClick={() => setShowAddModal(true)} className="btn-primary">
              Добавить канал
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Active Channels */}
          {activeChannels.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Активные каналы ({activeChannels.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {activeChannels.map((channel) => (
                  <ChannelCard
                    key={channel.id}
                    channel={channel}
                    onToggleActive={() => handleToggleActive(channel)}
                    onDelete={() => handleDelete(channel.id)}
                    onParse={() => handleParse(channel.id)}
                    onUpdateAutoParse={(data) => handleUpdateAutoParse(channel.id, data)}
                    isParsing={parseMutation.isPending}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Inactive Channels */}
          {inactiveChannels.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-500 mb-4">
                Неактивные каналы ({inactiveChannels.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 opacity-75">
                {inactiveChannels.map((channel) => (
                  <ChannelCard
                    key={channel.id}
                    channel={channel}
                    onToggleActive={() => handleToggleActive(channel)}
                    onDelete={() => handleDelete(channel.id)}
                    onParse={() => handleParse(channel.id)}
                    onUpdateAutoParse={(data) => handleUpdateAutoParse(channel.id, data)}
                    isParsing={parseMutation.isPending}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showAddModal && <AddChannelModal onClose={() => setShowAddModal(false)} />}
      {showDiscoveryModal && (
        <ChannelDiscoveryModal onClose={() => setShowDiscoveryModal(false)} />
      )}
    </div>
  )
}

function StatCard({
  label,
  value,
  color = 'gray',
}: {
  label: string
  value: number
  color?: string
}) {
  const colorClasses: Record<string, string> = {
    gray: 'bg-gray-50 text-gray-900',
    green: 'bg-green-50 text-green-900',
    blue: 'bg-blue-50 text-blue-900',
    purple: 'bg-purple-50 text-purple-900',
  }

  return (
    <div className={`rounded-lg p-4 ${colorClasses[color]}`}>
      <p className="text-xs opacity-75">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  )
}
